import { useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";

// India states GeoJSON — bundled in public/ for reliable offline-first loading.
// Pre-converted from topojson using topojson-client; uses `name` property per feature.
const INDIA_TOPO = "/india-states.json";

// ── Approximate center [lng, lat] for every Indian state / UT ───────────────
const STATE_COORDS = {
  "andhra pradesh":                              [80.0,  15.9],
  "arunachal pradesh":                           [94.7,  28.2],
  "assam":                                       [92.9,  26.2],
  "bihar":                                       [85.3,  25.1],
  "chhattisgarh":                                [81.9,  21.3],
  "goa":                                         [74.1,  15.3],
  "gujarat":                                     [71.2,  22.3],
  "haryana":                                     [76.1,  29.1],
  "himachal pradesh":                            [77.2,  31.1],
  "jharkhand":                                   [85.3,  23.6],
  "karnataka":                                   [75.7,  15.3],
  "kerala":                                      [76.3,  10.9],
  "madhya pradesh":                              [78.7,  23.0],
  "maharashtra":                                 [75.7,  19.8],
  "manipur":                                     [93.9,  24.7],
  "meghalaya":                                   [91.4,  25.5],
  "mizoram":                                     [92.9,  23.2],
  "nagaland":                                    [94.6,  26.2],
  "odisha":                                      [85.1,  20.9],
  "orissa":                                      [85.1,  20.9],
  "punjab":                                      [75.3,  31.1],
  "rajasthan":                                   [74.2,  27.0],
  "sikkim":                                      [88.5,  27.5],
  "tamil nadu":                                  [78.7,  11.1],
  "telangana":                                   [79.0,  18.1],
  "tripura":                                     [92.0,  23.9],
  "uttar pradesh":                               [80.9,  26.8],
  "uttarakhand":                                 [79.0,  30.1],
  "uttaranchal":                                 [79.0,  30.1],
  "west bengal":                                 [87.9,  23.0],
  "delhi":                                       [77.1,  28.7],
  "chandigarh":                                  [76.8,  30.7],
  "jammu and kashmir":                           [74.8,  34.1],
  "ladakh":                                      [77.6,  34.2],
  "lakshadweep":                                 [72.6,  10.6],
  "puducherry":                                  [79.8,  11.9],
  "pondicherry":                                 [79.8,  11.9],
  "andaman and nicobar islands":                 [92.7,  11.7],
  "dadra and nagar haveli and daman and diu":    [73.0,  20.4],
  "dadra and nagar haveli":                      [73.0,  20.4],
  "daman and diu":                               [72.8,  20.9],
};

// ── Normalize ip-api regionName → STATE_COORDS key ──────────────────────────
function normalizeState(name) {
  if (!name) return null;
  const lower = name.toLowerCase().trim();

  if (STATE_COORDS[lower]) return lower;

  // Fuzzy rules for common ip-api variants
  if (lower.includes("delhi"))              return "delhi";
  if (lower.includes("jammu"))              return "jammu and kashmir";
  if (lower.includes("andaman"))            return "andaman and nicobar islands";
  if (lower.includes("dadra") || lower.includes("daman") || lower.includes("diu"))
                                            return "dadra and nagar haveli and daman and diu";
  if (lower.includes("pondicherry") || lower.includes("puducherry"))
                                            return "puducherry";
  if (lower.includes("orissa"))             return "odisha";
  if (lower.includes("uttaranchal"))        return "uttarakhand";
  if (lower.includes("uttarakhand"))        return "uttarakhand";

  // Last resort: substring match on any key
  for (const key of Object.keys(STATE_COORDS)) {
    if (lower.includes(key) || key.includes(lower)) return key;
  }
  return null;
}

// ── Normalize topojson geo NAME_1 → same key space ──────────────────────────
function normalizeGeoName(name) {
  if (!name) return "";
  return name.toLowerCase()
    .replace("orissa", "odisha")
    .replace("uttaranchal", "uttarakhand")
    .replace("pondicherry", "puducherry")
    .trim();
}

const PIE_PALETTE = ["#6366f1","#38bdf8","#34d399","#fbbf24","#f87171","#a78bfa","#2dd4bf","#fb923c"];

// ── Main component ────────────────────────────────────────────────────────────
export default function IndiaVisitorMap({ locations = [] }) {
  const [tooltip, setTooltip] = useState(null);
  const [geoError, setGeoError] = useState(false);

  const maxCount = Math.max(...locations.map(l => l.count || 0), 1);

  // Build lookup: normalizedKey → location record (add coords)
  const stateMap = {};
  for (const loc of locations) {
    const key = normalizeState(loc.state);
    if (key && STATE_COORDS[key]) {
      stateMap[key] = { ...loc, coords: STATE_COORDS[key] };
    }
  }
  const bubbles = Object.values(stateMap);

  // ── Fallback: plain bar list if GeoJSON failed to load ──────────────────
  if (geoError) {
    return (
      <div className="space-y-2">
        {locations.length === 0 && (
          <p className="text-xs text-slate-400 py-6 text-center">No visitor data yet</p>
        )}
        {locations.slice(0, 6).map((l, i) => (
          <div key={i}>
            <div className="flex justify-between mb-0.5">
              <span className="text-[11px] text-slate-600 truncate max-w-[70%]">{l.state}</span>
              <span className="text-[11px] font-bold text-slate-800">{l.pct}%</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-1.5">
              <div className="h-1.5 rounded-full bg-indigo-500" style={{ width: `${l.pct}%` }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ── Empty state ────────────────────────────────────────────────────────────
  if (locations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-slate-400">
        <svg viewBox="0 0 24 24" className="w-8 h-8 mb-2 opacity-30" fill="currentColor">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
        </svg>
        <p className="text-xs">No visitor data yet</p>
        <p className="text-[10px] mt-0.5">Tracking activates on first storefront visit</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Map */}
      <div className="relative rounded-lg overflow-hidden bg-slate-50 border border-slate-100">
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{ center: [82, 23], scale: 1050 }}
          viewBox="0 0 800 680"
          style={{ width: "100%", height: "auto", display: "block" }}
        >
          <Geographies
            geography={INDIA_TOPO}
            onError={() => setGeoError(true)}
          >
            {({ geographies }) =>
              geographies.map(geo => {
                const geoKey = normalizeGeoName(
                  geo.properties?.name || geo.properties?.NAME_1 || ""
                );
                const hasData = !!stateMap[geoKey];
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={hasData ? "#dbeafe" : "#f1f5f9"}
                    stroke="#cbd5e1"
                    strokeWidth={0.6}
                    style={{
                      default:  { outline: "none" },
                      hover:    { outline: "none", fill: hasData ? "#bfdbfe" : "#e2e8f0" },
                      pressed:  { outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>

          {/* Visitor bubbles */}
          {bubbles.map((b, i) => {
            const r = 5 + Math.sqrt(b.count / maxCount) * 18;
            const color = PIE_PALETTE[i % PIE_PALETTE.length];
            return (
              <Marker
                key={i}
                coordinates={b.coords}
                onMouseEnter={() => setTooltip(b)}
                onMouseLeave={() => setTooltip(null)}
              >
                {/* Pulse ring */}
                <circle r={r + 4} fill={color} fillOpacity={0.15} />
                {/* Main bubble */}
                <circle
                  r={r}
                  fill={color}
                  fillOpacity={0.85}
                  stroke="white"
                  strokeWidth={1.5}
                  style={{ cursor: "pointer" }}
                />
                {/* Count label inside larger bubbles */}
                {r >= 12 && (
                  <text
                    textAnchor="middle"
                    dy={r > 15 ? "0.35em" : "0.35em"}
                    style={{
                      fontSize: Math.max(7, Math.min(11, r * 0.6)),
                      fill: "white",
                      fontWeight: 700,
                      pointerEvents: "none",
                      userSelect: "none",
                    }}
                  >
                    {b.count}
                  </text>
                )}
              </Marker>
            );
          })}
        </ComposableMap>

        {/* Tooltip */}
        {tooltip && (
          <div className="absolute top-2 left-2 pointer-events-none z-10">
            <div className="bg-slate-900/90 backdrop-blur-sm text-white text-xs rounded-xl px-3 py-2 shadow-lg border border-white/10">
              <p className="font-bold text-white leading-tight">{tooltip.state}</p>
              <p className="text-slate-300 mt-0.5">
                {tooltip.count} visitor{tooltip.count !== 1 ? "s" : ""} · {tooltip.pct}%
              </p>
            </div>
          </div>
        )}
      </div>

      {/* State list below map */}
      <div className="mt-3 space-y-1.5">
        {locations.slice(0, 5).map((l, i) => (
          <div key={i} className="flex items-center gap-2">
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ background: PIE_PALETTE[i % PIE_PALETTE.length] }}
            />
            <span className="text-[11px] text-slate-600 flex-1 truncate">{l.state}</span>
            <span className="text-[11px] font-bold text-slate-700 flex-shrink-0">
              {l.count} <span className="text-slate-400 font-normal">({l.pct}%)</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
